import { useCallback, MouseEvent, ReactNode } from "react"
import { UserAdviceInstance, UserAdviceState } from "../../Views/UserAdvice/Advice"
import { classNames } from "../../Components"
import { Block } from "../Block"
import { Icon, Icons } from "../Icon"
import { NavigationLightsIcon } from "../NavigationLightsIcon"

import { UserAdviceFormat } from "./Params"

const warningIcons = new Set<string>([
  Icons.AdviceAnchor, Icons.AdviceAnchorDrift, Icons.AdviceDepth, Icons.AdviceLights,
  Icons.AdviceLostPosition, Icons.AdvicePressure, Icons.AdviceWind,
]);

interface UserAdviceButtonProps {
  advice: UserAdviceInstance;
  className?: string;
  // How much wording to show (#1171), chosen for the whole row by
  // `UserAdviceData.adviceFormats`. Defaults to the full title, which is what every
  // caller rendered before formats existed.
  format?: UserAdviceFormat;
  state: UserAdviceState;
}

export function UserAdviceButton({ advice, className: _className, format, state }: UserAdviceButtonProps) {
  const clickCallback = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    state.setSelected(advice.id);
  }, [advice, state]);
  // Advice feature gates live on each advice class and are applied by
  // getUserAdvices(); every instance rendered here has already passed them.
  // The full wording is always the button's accessible name and tooltip, whatever is
  // actually painted: an icon-only button carrying no label would be unusable with a
  // screen reader, and unreadable to anyone who doesn't recognise the icon.
  const label = advice.props.title;
  const shown = adviceLabel(advice, format ?? UserAdviceFormat.full);
  const className = classNames(["UserAdviceButton", "button", `UserAdvice-${advice.props.type}`, `UserAdvice-${advice.props.level}`, `UserAdvice-format-${format ?? UserAdviceFormat.full}`]);
  const iconClassName = classNames([
    advice.props.type === "alert" && "UserAdviceIcon-alert",
    warningIcons.has(advice.props.icon) && "UserAdviceIcon-warning",
  ]);
  const body = (
    <>
      {advice.props.navigationLights == undefined
        ? <Icon className={iconClassName} icon={advice.props.icon} />
        : <NavigationLightsIcon className="UserAdviceIcon-navigation-lights" lighting={advice.props.navigationLights} />}
      {shown != undefined && <span className="UserAdviceButton-label"> {shown}{advice.props.navigationLights != undefined && " ?"}</span>}
      {advice.props.indicators?.map((indicator) => <span className="UserAdviceButton-indicator" key={indicator}>{indicator}</span>)}
      {advice.props.indicatorIcons?.map((indicator) => (
        <Icon className="UserAdviceButton-indicator-icon" icon={indicator.icon} key={indicator.label} title={indicator.label} />
      ))}
    </>
  );

  return (
    <button
      aria-label={label}
      className={className}
      onClick={clickCallback}
      title={label}
    >
      {body}
    </button>
  );
}

// The wording a given format paints. `short` falls back to the full title for an
// advice that declared no short form -- its title is already a bare noun.
function adviceLabel(advice: UserAdviceInstance, format: UserAdviceFormat): string | undefined {
  switch (format) {
    case UserAdviceFormat.icon:
      return undefined;
    case UserAdviceFormat.short:
      return advice.props.shortTitle || advice.props.title;
    case UserAdviceFormat.full:
      return advice.props.title;
  }
}

interface UserAdviceDialogProps {
  children?: ReactNode;
  className?: string;
  bodyRef?: React.RefObject<HTMLDivElement | null>;
  scrollFooter?: React.ReactNode;
}

// The body shared by every advice dialog. The surrounding `Overlay` + title are NOT
// rendered here: since #1171 the advice dialogs are mounted through
// `UserAdviceOverlayView` -> `OverlayView`, the same way the App and Ship hubs mount
// theirs, so the chrome belongs to the overlay host and each dialog declares its own
// overlay attributes (title, style, closability) from its module's `*Overlay` factory.
export function UserAdviceDialog({ children, className, bodyRef, scrollFooter }: UserAdviceDialogProps) {
  return (
    <>
      <div ref={bodyRef} className="Overlay-body UserAdviceDialog">
        <Block className={className}>
          {children}
        </Block>
      </div>
      {scrollFooter}
    </>
  );
}
